<template>
    <responsive :top="true" :hide-scroll="hideScroll" :enabled="true" v-bind="attrs()">
        <table ref="table" class="table table-data table-stripped table-hover mb-0">
            <colgroup v-if="showHeader">
                <col v-for="(header, index) in headers" :key="`col-${index}`" :style="getStyle(header)">
            </colgroup>
            <thead v-if="showHeader">
                <tr>
                    <th v-for="(header, index) in headers" :key="`th-${index}`" :class="{'buttons': header.type == 'buttons' || false}">
                        <ideo-form-checkbox
                            v-if="header.checkbox && !oldComponent"
                            v-model="checkedAll"
                            @change="checkAllItems"
                        ></ideo-form-checkbox>
                        <ideo-form-checkbox
                            v-else-if="header.checkbox"
                            v-model="checkedAllOld"
                            @change="checkAllItems"
                        ></ideo-form-checkbox>
                        <a href="" @click.stop.prevent="onSorting(header)" v-else-if="header.sort || false">
                            <i :class="getIcon(header)"></i>
                            {{ header.title || '' }}
                        </a>
                        <span v-else>
                            {{ header.title || '' }}
                        </span>
                    </th>
                </tr>
            </thead>
            <VueDraggable
                v-if="items.length > 0"
                v-model="items"
                :item-key="(el: any, index: number) => index"
                draggable=".draggable"
                :move="canSort"
                tag="tbody"
            >
                <template #item="{ element: item, index }">
                    <list-view-utils-row :key="`row-${index}`" :index="index" :item="item" :headers="props.headers">
                        <slot name="row" :item="item" :index="index"></slot>
                    </list-view-utils-row>
                </template>
            </VueDraggable>
            <tbody v-else>
                <td :colspan="headers.length" class="px-0">
                    <div class="text-center p-3 mt-1 bg-body-tertiary">{{ $t(emptyLabel) }}</div>
                </td>
            </tbody>
        </table>
    </responsive>
</template>

<script lang="ts" setup>
import { ref, computed, inject, useAttrs, Ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import VueDraggable from 'vuedraggable';
import Pager from '@/helpers/Pager';
import Row from '../utils/Row.vue';
import { only } from '@/helpers/Utils';
import { Header } from '../helpers';
import { useUsersStore } from '@/modules/core/users/store';

const store = useUsersStore();

export interface Props
{
    showHeader: boolean;
    headers: Header[];
    rows: any[];
    pager: Pager;
    checkedAllOld?: boolean;
    oldComponent?: boolean;
}

defineOptions({
    name: 'list-view-table-layout',
    components: {
        'list-view-utils-row': Row
    },
    inheritAttrs: false
});

const props = withDefaults(defineProps<Props>(), {
    checkedAllOld: false,
    oldComponent: false
});
const emptyLabel = inject<string>('empty-label');
const draggable = inject<boolean>('draggable');
const scrollBar = inject<Ref<HTMLDivElement | null>>('scrollBar', null);
const emit = defineEmits<{
    (e: 'change', header: any): void,
    (e: 'check', value: boolean): void
}>();

const onDrag = inject<any>('onDrag');
const checkedAll = inject<boolean>('checkedAll');

const checkedAllOld = computed(() =>
{
    return props.checkedAllOld;
});
const table = ref<HTMLTableElement | null>(null);
const { width: tableWidth } = useElementSize(table);

const items = computed({
    get()
    {
        return props.rows;
    },
    set(value: any[])
    {
        onDrag(value);
    }
});

const $attrs = useAttrs();
const attrs = (): Record<string, any> => only($attrs, 'class', 'style');
const headers = computed(() =>
{
    return props.headers
        .where(p => !p.disabled && p.visible !== false)
        .orderBy(p => p.position)
        .toArray();
});

const hideScroll = computed(() => !!scrollBar.value);

const getStyle = (header: any): Record<string, string> =>
{
    return {
        'width': header.width ? `${header.width || 0}${(header.width || 0).toString().endsWith('%') ? '' : 'px'}` : ''
    };
};

const canSort = (): boolean =>
{
    return draggable;
};

const getIcon = (header: any): string =>
{
    return "fa fa-fw fa-sort" + (props.pager.sorting !== (header.sort || '') ? '' : props.pager.order.toLowerCase() === 'asc' ? '-up' : '-down');
};

const onSorting = (header: any): any =>
{
    emit('change', header);
};

const allCheckbox = computed({
    get: () => store.checkAll,
    set: (value) => store.checkAll = value
});

const checkAllItems = (value: boolean): void =>
{
    emit('check', value);
};

const setScrollLeft = (value: number): void =>
{
    if (!table.value) return;

    table.value.parentElement.parentElement.scrollLeft = value;
};

defineExpose({
    width: tableWidth,
    setScrollLeft,
});
</script>
